import './CopCaseDetails.scss'

import { useEffect, useMemo, useRef, useState } from 'react'

import { getErrorMessage } from '../../constants/common-error-messages'
import { ToastService } from '../../services/toast.service'
import { useSectionPermissions } from '../Shared/PermissionAccessGuard/hooks/useSectionPermissions';
import { CaseAddCommentModal } from './case-add-comment-modal'
import { CaseCommentsFilesTab } from './case-comments-files-tab'
import { CaseDetailsTab } from './case-details-tab'
import { CaseHistoryTab } from './case-history-tab'
import { CaseRejectModal } from './case-reject-modal'
import { CaseResolutionBanner } from './case-resolution-banner'
import { CaseTabs, TabKey } from './case-tabs'
import {
  parseCancelConfig,
  parseLabels,
  parseResolutionConfig,
  resolveCaseId,
  resolveCommentTableLabels,
  resolvePopupLabels,
  resolveRejectModalLabels,
  resolveResolutionBannerLabels,
  resolveTabLabels,
} from './CopCaseDetails.resolvers'
import type { CopCaseDetailsProps } from './CopCaseDetails.types'
import { CaseHeaderSummary } from './header-summary'
import {
  approveCaseResolution,
  CaseDetailsResponse,
  fetchCaseDetails,
  rejectCaseResolution,
} from './services/CopCaseDetails.service'
export type { CopCaseDetailsProps }

const CASE_RESOLUTION_RELOAD_DELAY = 2000

export default function CopCaseDetails(props: Readonly<CopCaseDetailsProps>) {
  const { title, sectiontype, layout, labels } = props
  const tab = resolveTabLabels(props)
  const popup = resolvePopupLabels(props)
  const table = resolveCommentTableLabels(props)
  const banner = resolveResolutionBannerLabels(props)
  const rejectModal = resolveRejectModalLabels(props)
  const resConfig = parseResolutionConfig(props.resolutionconfig)
  const rc = resConfig.rejectionLabels ?? {}
  const cancelCfg = parseCancelConfig(props.cancelconfig)

  const isCasesSection = !sectiontype || sectiontype === 'cases'

  const [activeTab, setActiveTab] = useState<TabKey>('details')
  const [caseDetails, setCaseDetails] = useState<CaseDetailsResponse | null>(null)
  const detailsLoadedRef = useRef(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const caseId = useMemo(() => resolveCaseId(), [])
  const {permissions }= useSectionPermissions();

  const parsedLabels = useMemo(
    () => (isCasesSection ? parseLabels(labels) : []),
    [isCasesSection, labels],
  )
  const resolvedLayout = isCasesSection ? layout : undefined

  useEffect(() => {
    if (activeTab !== 'details' || detailsLoadedRef.current) {
      return
    }
    detailsLoadedRef.current = true
    let cancelled = false

    fetchCaseDetails(caseId)
      .then(response => {
        if (cancelled) return
        setCaseDetails(response)
      })
      .catch(err => {
        const fallbackMessage = getErrorMessage('case-details-loading')
        const message = err instanceof Error && err.message ? err.message : fallbackMessage
        ToastService.error(message)
      })

    return () => {
      cancelled = true
    }
  }, [activeTab, caseId])

  useEffect(() => {
    const caseNumber = (caseDetails?.case as { number?: string } | undefined)?.number
    if (!caseNumber) return

    const breadcrumbList = document.querySelector('[itemtype*="BreadcrumbList"]')
    if (!breadcrumbList) return

    const spans = breadcrumbList.querySelectorAll('[itemprop="name"]')
    const lastSpan = spans[spans.length - 1]
    if (lastSpan) {
      lastSpan.textContent = caseNumber
    }
  }, [caseDetails])

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      await approveCaseResolution(caseId)
      setBannerDismissed(true)
      ToastService.success(resConfig.successText ?? banner.approveSuccessMessage)
      setTimeout(() => window.location.reload(), CASE_RESOLUTION_RELOAD_DELAY)
    } catch (err) {
      const fallbackMessage = getErrorMessage('case-approval')
      const message = err instanceof Error && err.message ? err.message : (resConfig.errorText ?? banner.approveErrorMessage ?? fallbackMessage)
      ToastService.error(message)
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async (reason: string) => {
    setIsRejecting(true)
    try {
      await rejectCaseResolution(caseId, reason)
      setRejectModalOpen(false)
      setBannerDismissed(true)
      ToastService.success(resConfig.successText ?? banner.rejectSuccessMessage)
      setTimeout(() => window.location.reload(), CASE_RESOLUTION_RELOAD_DELAY)
    } catch (err) {
      const fallbackMessage = getErrorMessage('case-rejection')
      const message = err instanceof Error && err.message ? err.message : (resConfig.errorText ?? banner.rejectErrorMessage ?? fallbackMessage)
      ToastService.error(message)
    } finally {
      setIsRejecting(false)
    }
  }

  const caseObj = caseDetails?.case as { status?: string; number?: string | null; subject?: string | null } | undefined
  const caseStatus = caseObj?.status ?? ''
  const showResolutionBanner = !bannerDismissed && caseStatus.toLowerCase().includes('resolved')

  return (
    <div className="cop-case-details">
      {showResolutionBanner && (
        <CaseResolutionBanner
          message={resConfig.message ?? banner.message}
          rejectLabel={resConfig.rejectLabel ?? banner.rejectLabel}
          acceptLabel={resConfig.acceptLabel ?? banner.acceptLabel}
          onReject={() => setRejectModalOpen(true)}
          onAccept={handleApprove}
          acceptDisabled={isApproving}
          canViewCaseResolution={permissions.canViewCaseResolution}
        />
      )}
      <CaseHeaderSummary
        title={title}
        layout={resolvedLayout}
        labels={parsedLabels}
        apiResponse={caseDetails}
      />
      <CaseTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tab1Label={tab.tab1Label}
        tab2Label={tab.tab2Label}
        tab3Label={tab.tab3Label}
        addCommentLabel={tab.addCommentLabel}
        onAddComment={() => setModalOpen(true)}
      />
      {activeTab === 'details' && <CaseDetailsTab apiResponse={caseDetails} accordionLabels={tab.accordionLabels} />}
      {activeTab === 'comments' && (
        <CaseCommentsFilesTab
          caseId={caseId}
          refreshKey={commentsRefreshKey}
          addCommentLabel={tab.addCommentLabel}
          onAddComment={() => setModalOpen(true)}
          colLastModifiedBy={table.colLastModifiedBy}
          colDate={table.colDate}
          colComments={table.colComments}
          colAttachments={table.colAttachments}
          colViewDownload={table.colViewDownload}
          viewLabel={table.viewLabel}
          downloadLabel={table.downloadLabel}
          noCommentsLabel={table.noCommentsLabel}
          numberOfCommentsLabel={table.numberOfCommentsLabel}
          itemsPerPageLabel={table.itemsPerPageLabel}
          loadingLabel={table.loadingLabel}
          fileDownloadErrorLabel={table.fileDownloadErrorLabel}
        />
      )}
      {activeTab === 'history' && <CaseHistoryTab caseId={caseId} />}

      {modalOpen && (
        <CaseAddCommentModal
          caseId={caseId}
          title={popup.title}
          commentHelpText={popup.description}
          textareaPlaceholder={popup.placeholder}
          charLimit={popup.charLimit ? Number.parseInt(popup.charLimit, 10) : undefined}
          fileHelpText={popup.fileHelpText}
          cancelLabel={popup.cancelLabel}
          shareLabel={popup.shareLabel}
          commentRequiredError={popup.commentRequiredError}
          fileTypeError={popup.fileTypeError}
          fileSizeErrorMessage={popup.fileSizeErrorMessage}
          fileRequiredError={popup.fileRequiredError}
          submitSuccessMessage={popup.submitSuccessMessage}
          submitErrorMessage={popup.submitErrorMessage}
          selectFileLabel={popup.selectFileLabel}
          noFileSelectedLabel={popup.noFileSelectedLabel}
          cancelConfirmMessage={cancelCfg.message ?? popup.cancelConfirmMessage}
          cancelConfirmYesLabel={cancelCfg.yesLabel ?? popup.cancelConfirmYesLabel}
          cancelConfirmNoLabel={cancelCfg.noLabel ?? popup.cancelConfirmNoLabel}
          cancelConfirmSubtext={cancelCfg.subtext}
          onCommentAdded={() => setCommentsRefreshKey(k => k + 1)}
          onClose={() => setModalOpen(false)}
        />
      )}

      {rejectModalOpen && (
        <CaseRejectModal
          caseNumber={caseObj?.number}
          caseSubject={caseObj?.subject}
          title={rc.title ?? rejectModal.title}
          helpText={rc.reasonLabel ?? rejectModal.helpText}
          placeholder={rc.reasonPlaceholder ?? rejectModal.placeholder}
          charLimit={rejectModal.charLimit ? Number.parseInt(rejectModal.charLimit, 10) : undefined}
          cancelLabel={rc.cancelLabel ?? rejectModal.cancelLabel}
          sendLabel={rc.confirmLabel ?? rejectModal.sendLabel}
          caseText={rc.caseText}
          subjectLabel={rc.subjectLabel}
          sendDisabled={isRejecting}
          requiredError={rc.reasonRequiredText}
          cancelConfirmMessage={cancelCfg.message}
          cancelConfirmSubtext={cancelCfg.subtext}
          cancelConfirmYesLabel={cancelCfg.yesLabel}
          cancelConfirmNoLabel={cancelCfg.noLabel}
          onClose={() => setRejectModalOpen(false)}
          onSubmit={handleReject}
        />
      )}
    </div>
  )
}